<template>
  <v-card>
    <v-card-title class="pb-2">
      <v-icon class="mr-1">mdi-file-tree</v-icon>
      Workflow
      <v-chip
        v-if="ticket.workflow && ticket.workflow.length"
        x-small
        label
        class="ml-2"
      >
        {{ done_count }} / {{ ticket.workflow.length }}
      </v-chip>
      <v-spacer />

      <v-menu
        v-model="quick_add_menu"
        :close-on-content-click="false"
        offset-y
        left
        max-width="360"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" small text color="primary">
            <v-icon left small>mdi-account-plus</v-icon>
            Bearbeiter hinzufügen
          </v-btn>
        </template>
        <v-card>
          <v-card-text class="pb-1">
            <div class="text-caption grey--text mb-3">
              Hängt einen weiteren Schritt ans Ende des Workflows – die gewählte Person bzw. Gruppe erhält das Ticket im Anschluss.
            </div>
            <v-btn-toggle v-model="qa_type" mandatory dense class="mb-3">
              <v-btn small value="user"><v-icon left small>mdi-account</v-icon>Benutzer</v-btn>
              <v-btn small value="group"><v-icon left small>mdi-account-group</v-icon>AD-Gruppe</v-btn>
            </v-btn-toggle>
            <v-autocomplete
              v-if="qa_type === 'user'"
              v-model="qa_user_id"
              :items="users"
              item-text="displayName"
              item-value="id"
              label="Benutzer"
              outlined
              dense
              hide-details
              clearable
            />
            <v-autocomplete
              v-else
              v-model="qa_group"
              :items="availableGroups"
              label="AD-Gruppe"
              outlined
              dense
              hide-details
              clearable
            />
            <v-text-field
              v-model="qa_name"
              label="Bezeichnung (optional)"
              placeholder="z.B. Freigabe"
              outlined
              dense
              hide-details
              class="mt-3"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text small @click="quick_add_menu = false">Abbrechen</v-btn>
            <v-btn
              color="primary"
              small
              :loading="quick_adding"
              :disabled="!canQuickAdd"
              @click="quickAdd"
            >
              <v-icon left small>mdi-plus</v-icon>
              Hinzufügen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>

      <v-btn
        v-if="canEditTail"
        small
        text
        @click="openEdit"
      >
        <v-icon left small>mdi-pencil</v-icon>
        Workflow ändern
      </v-btn>
    </v-card-title>

    <v-card-text>
      <div v-if="!hasWorkflow" class="text-caption grey--text">
        Dieses Ticket hat keinen Workflow.
        <v-btn x-small text color="primary" class="ml-2" @click="openEdit">
          Workflow hinzufügen
        </v-btn>
      </div>

      <div v-else>
        <div class="wf-progress mb-3">
          <template v-for="(node, i) in ticket.workflow">
            <div
              :key="'step-' + i"
              class="wf-step"
              :class="stepClass(i)"
              @click="active_index = i"
            >
              <v-icon v-if="isDone(i)" small class="mr-1" color="success">mdi-check-circle</v-icon>
              <v-icon v-else-if="isActive(i)" small class="mr-1" color="primary">mdi-progress-clock</v-icon>
              <v-icon v-else small class="mr-1" color="grey">mdi-circle-outline</v-icon>
              <span class="wf-step-name">{{ node.name || '(unbenannt)' }}</span>
            </div>
            <v-icon
              v-if="i < ticket.workflow.length - 1"
              :key="'arrow-' + i"
              small
              class="wf-arrow"
              color="grey"
            >mdi-chevron-right</v-icon>
          </template>
        </div>

        <v-card v-if="selected_node" outlined class="node-detail">
          <div class="px-3 py-2 d-flex align-center node-detail-header">
            <v-chip x-small label class="step-chip mr-2">{{ active_index + 1 }}</v-chip>
            <span class="text-subtitle-1 font-weight-bold">{{ selected_node.name || '(unbenannt)' }}</span>
            <v-spacer />
            <v-chip
              v-if="isDone(active_index)"
              x-small
              color="success"
              text-color="white"
            >
              <v-icon x-small left>mdi-check</v-icon>
              Abgeschlossen
            </v-chip>
            <v-chip
              v-else-if="isActive(active_index)"
              x-small
              color="primary"
              text-color="white"
            >
              <v-icon x-small left>mdi-progress-clock</v-icon>
              Aktiv
            </v-chip>
            <v-chip v-else x-small outlined>
              Ausstehend
            </v-chip>
          </div>
          <v-divider />
          <div class="px-3 py-3">
            <div class="d-flex align-center mb-2">
              <v-icon x-small class="mr-1">
                {{ selected_node.assignee_type === 'group' ? 'mdi-account-group' : 'mdi-account' }}
              </v-icon>
              <span class="text-body-2">{{ assigneeLabel(selected_node) || 'kein Bearbeiter' }}</span>
              <v-spacer />
              <span v-if="isDone(active_index)" class="text-caption grey--text">
                {{ formatCompletion(selected_node) }}
              </span>
            </div>

            <template v-if="selected_node.checklist && selected_node.checklist.length">
              <v-divider class="my-2" />
              <div class="text-caption grey--text mb-1">
                Checkliste ({{ checkedCount(selected_node) }} / {{ selected_node.checklist.length }})
              </div>
              <div
                v-for="(item, j) in selected_node.checklist"
                :key="j"
                class="checklist-row"
                :class="{ 'checklist-row-disabled': !canToggle(active_index) }"
              >
                <v-checkbox
                  :input-value="item.checked"
                  :disabled="!canToggle(active_index) || toggling[`${active_index}-${j}`]"
                  :loading="toggling[`${active_index}-${j}`]"
                  hide-details
                  dense
                  class="mt-0 pt-0"
                  @change="toggleItem(active_index, j)"
                >
                  <template v-slot:label>
                    <span class="d-flex align-center">
                      <v-icon v-if="item.icon" x-small class="mr-1">{{ item.icon }}</v-icon>
                      <span :class="{ 'text-decoration-line-through grey--text': item.checked }">
                        {{ item.label }}
                      </span>
                    </span>
                  </template>
                </v-checkbox>
                <span
                  v-if="item.checked && item.checked_at"
                  class="text-caption grey--text ml-2"
                  :title="formatDateTime(item.checked_at)"
                >
                  {{ user_name(item.checked_by) }} · {{ formatDate(item.checked_at) }}
                </span>
              </div>
            </template>
            <template v-else>
              <v-divider class="my-2" />
              <div class="text-caption grey--text">Keine Checkliste in diesem Knoten.</div>
            </template>

            <div v-if="isActive(active_index)" class="d-flex mt-3">
              <v-spacer />
              <v-btn
                color="primary"
                small
                :loading="advancing"
                @click="advance"
              >
                <v-icon left small>mdi-arrow-right-circle</v-icon>
                Knoten abschließen
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </v-card-text>

    <v-dialog v-model="edit_dialog" max-width="780" persistent>
      <v-card v-if="edit_dialog">
        <v-card-title>
          Workflow bearbeiten
          <v-chip x-small label class="ml-2" outlined>
            ab Schritt {{ tail_start_index + 1 }}
          </v-chip>
          <v-spacer />
          <v-btn icon @click="cancelEdit">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p class="text-caption grey--text mb-3" v-if="done_count > 0">
            Bereits abgeschlossene Knoten ({{ done_count }}) bleiben unverändert. Du kannst nur den aktuellen und alle nachfolgenden Knoten bearbeiten.
          </p>
          <div v-if="draft_tail.length === 0" class="text-caption grey--text mb-3">
            Keine offenen Knoten. Füge mindestens einen hinzu.
          </div>
          <v-card
            v-for="(node, i) in draft_tail"
            :key="i"
            outlined
            class="mb-2 node-edit-card"
          >
            <div class="d-flex align-center px-3 py-2">
              <v-chip x-small label class="step-chip mr-2">{{ tail_start_index + i + 1 }}</v-chip>
              <v-text-field
                v-model="node.name"
                placeholder="Knoten-Name"
                outlined
                dense
                hide-details
                class="flex-grow-1"
              />
              <v-btn icon small :disabled="i === 0" @click="moveTail(i, -1)" title="Nach oben">
                <v-icon small>mdi-arrow-up</v-icon>
              </v-btn>
              <v-btn icon small :disabled="i === draft_tail.length - 1" @click="moveTail(i, 1)" title="Nach unten">
                <v-icon small>mdi-arrow-down</v-icon>
              </v-btn>
              <v-btn icon small @click="removeTail(i)" title="Entfernen">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
            <v-divider />
            <div class="px-3 py-3">
              <v-row dense>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="node.assignee_type"
                    :items="assigneeTypeOptions"
                    label="Bearbeiter-Typ"
                    outlined
                    dense
                    hide-details
                    @change="onAssigneeTypeChange(node)"
                  />
                </v-col>
                <v-col cols="12" md="8">
                  <v-autocomplete
                    v-if="node.assignee_type === 'user'"
                    v-model="node.assignee_user_id"
                    :items="users"
                    item-text="displayName"
                    item-value="id"
                    label="Benutzer"
                    outlined
                    dense
                    hide-details
                    clearable
                  />
                  <v-autocomplete
                    v-else
                    v-model="node.assignee_group"
                    :items="availableGroups"
                    label="AD-Gruppe"
                    outlined
                    dense
                    hide-details
                    clearable
                  />
                </v-col>
              </v-row>
              <div class="d-flex align-center mt-3 mb-1">
                <span class="text-caption grey--text">Checkliste ({{ node.checklist.length }})</span>
                <v-spacer />
                <v-btn x-small text color="primary" @click="addChecklistItem(node)">
                  <v-icon left x-small>mdi-plus</v-icon>
                  Item
                </v-btn>
              </div>
              <div
                v-for="(item, j) in node.checklist"
                :key="j"
                class="d-flex align-center mb-1"
                style="gap: 4px;"
              >
                <v-text-field
                  v-model="item.label"
                  placeholder="Bezeichnung"
                  outlined
                  dense
                  hide-details
                  class="flex-grow-1"
                />
                <v-text-field
                  v-model="item.icon"
                  placeholder="mdi-icon (optional)"
                  outlined
                  dense
                  hide-details
                  style="max-width: 200px;"
                  :prepend-inner-icon="item.icon || 'mdi-checkbox-blank-outline'"
                />
                <v-btn icon small @click="removeChecklistItem(node, j)">
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card>
          <v-btn small text color="primary" @click="addTailNode">
            <v-icon left small>mdi-plus</v-icon>
            Knoten hinzufügen
          </v-btn>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn text @click="cancelEdit">Abbrechen</v-btn>
          <v-btn color="primary" :loading="saving_tail" :disabled="!canSaveTail" @click="saveTail">
            <v-icon left small>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
const cloneNode = (n) => ({
  name: n.name || "",
  assignee_type: n.assignee_type || "group",
  assignee_user_id: n.assignee_user_id || null,
  assignee_group: n.assignee_group || null,
  checklist: (n.checklist || []).map(it => ({
    label: it.label || "",
    icon: it.icon || "",
    checked: !!it.checked,
  })),
})

export default {
  name: "TicketWorkflow",
  props: {
    ticket: { type: Object, required: true },
  },
  data: () => ({
    active_index: 0,
    advancing: false,
    saving_tail: false,
    toggling: {},
    edit_dialog: false,
    draft_tail: [],
    tail_start_index: 0,
    quick_add_menu: false,
    quick_adding: false,
    qa_type: "user",
    qa_user_id: null,
    qa_group: null,
    qa_name: "",
    assigneeTypeOptions: [
      { text: "Benutzer", value: "user" },
      { text: "AD-Gruppe", value: "group" },
    ],
  }),
  computed: {
    hasWorkflow() {
      return Array.isArray(this.ticket.workflow) && this.ticket.workflow.length > 0
    },
    canEditTail() {
      return this.ticket.state === "OPEN"
    },
    current_index() {
      if (!this.hasWorkflow) return -1
      const i = this.ticket.workflow.findIndex(n => !n.completed_at)
      return i === -1 ? this.ticket.workflow.length - 1 : i
    },
    done_count() {
      if (!this.hasWorkflow) return 0
      return this.ticket.workflow.filter(n => n.completed_at).length
    },
    selected_node() {
      if (!this.hasWorkflow) return null
      return this.ticket.workflow[this.active_index] || null
    },
    users() {
      return this.$store.state.ticketsystem.users
    },
    availableGroups() {
      const set = new Set()
      for (const u of this.users) {
        if (Array.isArray(u.pms)) for (const p of u.pms) set.add(p)
      }
      return [...set].sort()
    },
    canSaveTail() {
      return this.draft_tail.length > 0 && this.draft_tail.every(n => (n.name || "").trim().length > 0)
    },
    canQuickAdd() {
      return this.qa_type === "user"
        ? this.qa_user_id != null
        : !!this.qa_group
    },
  },
  watch: {
    "ticket.id": {
      immediate: true,
      handler() {
        this.active_index = this.current_index >= 0 ? this.current_index : 0
      },
    },
    current_index(i) {
      if (i >= 0 && i < (this.ticket.workflow || []).length) {
        this.active_index = i
      }
    },
  },
  methods: {
    isDone(i) {
      const node = this.ticket.workflow && this.ticket.workflow[i]
      return !!(node && node.completed_at)
    },
    isActive(i) {
      return i === this.current_index && !this.isDone(i)
    },
    canToggle(i) {
      return this.isActive(i) && this.ticket.state === "OPEN"
    },
    stepClass(i) {
      return {
        "wf-step-done":    this.isDone(i),
        "wf-step-active":  this.isActive(i),
        "wf-step-pending": !this.isDone(i) && !this.isActive(i),
        "wf-step-selected": this.active_index === i,
      }
    },
    assigneeLabel(node) {
      if (!node) return ""
      if (node.assignee_type === "user") {
        return this.user_name(node.assignee_user_id)
      }
      return node.assignee_group || ""
    },
    user_name(id) {
      if (id == null) return ""
      const u = this.users.find(x => x.id === id)
      return u ? u.displayName : ""
    },
    checkedCount(node) {
      return (node.checklist || []).filter(it => it.checked).length
    },
    formatDate(s) {
      if (!s) return ""
      return new Date(s).toLocaleDateString("de-DE", {
        day: "2-digit", month: "2-digit", year: "2-digit",
      })
    },
    formatDateTime(s) {
      if (!s) return ""
      return new Date(s).toLocaleString("de-DE")
    },
    formatCompletion(node) {
      if (!node || !node.completed_at) return ""
      const who = this.user_name(node.completed_by)
      const when = this.formatDate(node.completed_at)
      return who ? `${who} · ${when}` : when
    },

    async toggleItem(node_index, item_index) {
      const key = `${node_index}-${item_index}`
      this.$set(this.toggling, key, true)
      try {
        await this.$store.dispatch("update", {
          payload_type: "PATCH",
          context: `/tickets/${this.ticket.id}/checklist/${node_index}/${item_index}`,
          payload: {},
        })
      } finally {
        this.$set(this.toggling, key, false)
      }
    },

    async advance() {
      this.advancing = true
      try {
        await this.$store.dispatch("update", {
          payload_type: "POST",
          context: `/tickets/${this.ticket.id}/advance`,
        })
      } finally {
        this.advancing = false
      }
    },

    async quickAdd() {
      if (!this.canQuickAdd || this.quick_adding) return
      this.quick_adding = true
      try {
        const isUser = this.qa_type === "user"
        const label = isUser ? this.user_name(this.qa_user_id) : this.qa_group
        const node = {
          name: (this.qa_name || "").trim() || label || "Bearbeiter",
          assignee_type: this.qa_type,
          assignee_user_id: isUser ? this.qa_user_id : null,
          assignee_group: isUser ? null : this.qa_group,
          checklist: [],
        }
        await this.$store.dispatch("update", {
          payload_type: "POST",
          context: `/tickets/${this.ticket.id}/workflow/append`,
          payload: node,
        })
        this.quick_add_menu = false
        this.qa_user_id = null
        this.qa_group = null
        this.qa_name = ""
      } finally {
        this.quick_adding = false
      }
    },

    openEdit() {
      const wf = Array.isArray(this.ticket.workflow) ? this.ticket.workflow : []
      const start = wf.findIndex(n => !n.completed_at)
      this.tail_start_index = start === -1 ? wf.length : start
      const tail = start === -1 ? [] : wf.slice(start)
      this.draft_tail = tail.length ? tail.map(cloneNode) : [this.emptyNode()]
      this.edit_dialog = true
    },
    cancelEdit() {
      this.edit_dialog = false
      this.draft_tail = []
    },
    emptyNode() {
      return {
        name: "",
        assignee_type: "group",
        assignee_user_id: null,
        assignee_group: null,
        checklist: [],
      }
    },
    addTailNode() {
      this.draft_tail.push(this.emptyNode())
    },
    removeTail(i) {
      this.draft_tail.splice(i, 1)
    },
    moveTail(i, dir) {
      const j = i + dir
      if (j < 0 || j >= this.draft_tail.length) return
      const tmp = this.draft_tail[i]
      this.$set(this.draft_tail, i, this.draft_tail[j])
      this.$set(this.draft_tail, j, tmp)
    },
    onAssigneeTypeChange(node) {
      if (node.assignee_type === "user") node.assignee_group = null
      else node.assignee_user_id = null
    },
    addChecklistItem(node) {
      node.checklist.push({ label: "", icon: "", checked: false })
    },
    removeChecklistItem(node, j) {
      node.checklist.splice(j, 1)
    },
    async saveTail() {
      this.saving_tail = true
      try {
        const body = this.draft_tail.map(n => ({
          name: n.name,
          assignee_type: n.assignee_type,
          assignee_user_id: n.assignee_type === "user" ? n.assignee_user_id : null,
          assignee_group: n.assignee_type === "group" ? n.assignee_group : null,
          checklist: (n.checklist || []).map(it => ({
            label: it.label,
            icon: it.icon || null,
            checked: !!it.checked,
          })),
        }))
        await this.$store.dispatch("update", {
          payload_type: "PATCH",
          context: `/tickets/${this.ticket.id}/workflow`,
          payload: body,
        })
        this.edit_dialog = false
        this.draft_tail = []
      } finally {
        this.saving_tail = false
      }
    },
  },
}
</script>

<style scoped>
.wf-progress {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.wf-step {
  display: flex;
  align-items: center;
  padding: 4px 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 16px;
  cursor: pointer;
  background: #fff;
  transition: all 0.15s ease;
  user-select: none;
}
.wf-step:hover {
  border-color: rgba(123, 0, 0, 0.5);
}
.wf-step-name {
  font-size: 0.85rem;
  white-space: nowrap;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wf-step-done {
  background: rgba(76, 175, 80, 0.08);
  border-color: rgba(76, 175, 80, 0.4);
}
.wf-step-active {
  background: rgba(123, 0, 0, 0.08);
  border-color: rgba(123, 0, 0, 0.5);
  font-weight: 600;
}
.wf-step-pending {
  background: rgba(0, 0, 0, 0.02);
}
.wf-step-selected {
  box-shadow: 0 0 0 2px rgba(123, 0, 0, 0.35);
}
.wf-arrow.v-icon {
  align-self: center;
}
.step-chip.v-chip {
  background: rgba(123, 0, 0, 0.12) !important;
  color: #7b0000 !important;
  font-weight: 700;
}
.node-detail-header {
  background: rgba(0, 0, 0, 0.02);
}
.checklist-row {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}
.checklist-row-disabled .v-input--checkbox {
  opacity: 0.85;
}
.node-edit-card {
  background: rgba(0, 0, 0, 0.02);
}
</style>
